// Filter a full field catalog to the user-visible set for table rendering.
//
// Mirrors the server-side logic originally in api/ai/tools/contract.js — kept in
// the frontend because `display` is a Table-only rendering concern that should
// not narrow the LLM's field metadata or other view types (pivot, schedule).
//
// Rules:
// 1. Empty/unset display → visible-by-default fields only.
// 2. Display list → listed fields in display order, marked visible.
// 3. When any displayed field is `grouped`, all grouped fields are force-included
//    so implied groupings (e.g. project → client) render together.
//
// A field marked `visible: false` is never force-included: it is internal (rowType,
// projectId, clientId) and belongs in the data, not in a column the user reads. Table
// styling reads `row.rowType` off the row itself, so nothing here has to carry it.
export function applyDisplay(fields, display) {
  if (!Array.isArray(display) || display.length === 0) {
    return fields.filter((f) => f.visible !== false);
  }

  const displaySet = new Set(display);

  const displayed = display
    .map((key) => fields.find((f) => f.key === key))
    .filter(Boolean)
    .map((f) => ({ ...f, visible: true }));

  // A list tool's `select`/`expand` can leave every displayed key out of the result.
  if (displayed.length === 0) return fields.filter((f) => f.visible !== false);

  const hasGroupedField = displayed.some((f) => f.grouped);
  for (const f of fields) {
    if (displaySet.has(f.key) || f.visible === false) continue;
    if (hasGroupedField && f.grouped) displayed.push(f);
  }

  return displayed;
}
